import { Link } from 'react-router-dom';

const QUICK_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/packages', label: 'Packages' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/contact', label: 'Book / Quote' },
  { to: '/get-in-touch', label: 'Contact' },
];

const EVENT_LINKS = [
  { to: '/agricultural-shows', label: 'Agricultural Shows' },
  { to: '/festivals-and-community-events', label: 'Festivals & Events' },
  { to: '/schools-and-kinders', label: 'Schools & Kinders' },
  { to: '/national-science-week', label: 'Science Week' },
  { to: '/charity-and-community', label: 'Charity & Community' },
];

const LEGAL_LINKS = [
  { to: '/faqs', label: 'FAQs' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
];

const Footer = () => {
  return (
    <footer className="relative bg-charcoal text-cream/80">
      {/* Tropical foliage top edge */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-jungle-900/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img
                src="/images/jacklogo.png"
                alt="Jurassic Jack"
                className="h-14 w-auto"
              />
            </Link>
            <p className="text-base leading-relaxed text-cream/60">
              Premium roaming dinosaur entertainment for agricultural shows, festivals, schools
              and community events across Victoria.
            </p>
            <p className="text-sm text-cream/40 mt-3">
              Based in Geelong, VIC • Available across Victoria • Travel + accommodation quoted when required
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-sm uppercase tracking-widest text-amber mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {QUICK_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-base text-cream/60 hover:text-amber transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Event Types */}
          <div>
            <h4 className="font-display text-sm uppercase tracking-widest text-amber mb-4">
              Events
            </h4>
            <ul className="space-y-2">
              {EVENT_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-base text-cream/60 hover:text-amber transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-sm uppercase tracking-widest text-amber mb-4">
              Contact
            </h4>
            <div className="space-y-3 text-base">
              <p className="flex items-start gap-2">
                <svg className="w-4 h-4 mt-0.5 text-jp-red shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-cream/60">Based in Geelong, VIC<br />Available across Victoria</span>
              </p>
              <p className="flex items-center gap-2">
                <svg className="w-4 h-4 text-jp-red shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href="tel:+61414995458" className="text-cream/60 hover:text-amber transition-colors">
                  0414 995 458
                </a>
              </p>
              <p className="flex items-center gap-2">
                <svg className="w-4 h-4 text-jp-red shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:bookings@jurassicjack.com.au" className="text-cream/60 hover:text-amber transition-colors">
                  bookings@jurassicjack.com.au
                </a>
              </p>
            </div>
            <div className="mt-6">
              <Link to="/contact" className="btn-primary text-sm py-2.5 px-5">
                Request a Quote
              </Link>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="border-t border-charcoal-700 mt-12 pt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-cream/40">
              © {new Date().getFullYear()} Jurassic Jack. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              {LEGAL_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-sm text-cream/40 hover:text-amber transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
